import imageFileToImageData from 'utils/imageDecode'
import { normalizeToOtpauthUri } from './index'

const MIGRATION_SCHEME = 'otpauth-migration://'
const TOTP_SCHEME = 'otpauth://'

/**
 * otpauth URI 에 비지 않은 secret 파라미터가 있는지. 파싱 실패 사유를 "시드 문제"와 "그 밖의
 * 파라미터 문제"로 가르는 판별자다.
 *
 * 라이브러리 에러 문구에 의존하지 않는 이유: normalizeToOtpauthUri 는 OTPAuth 의 원본 에러를
 * INVALID_TOTP_INPUT 하나로 감싸므로(services/totp/index.js:57-60) code 로는 구분이 불가하고,
 * 문구 매칭("Invalid 'period' parameter" 등)은 라이브러리 버전에 묶인다.
 */
function hasNonEmptySecretParam(raw) {
  const matched = /[?&]secret=([^&#]*)/i.exec(raw)
  if (matched == null) return false
  try {
    return decodeURIComponent(matched[1]).trim() !== ''
  } catch (e) {
    return matched[1].trim() !== '' // 잘못된 % 인코딩 — 원문 기준으로 판단
  }
}

/**
 * QR 이 담고 있던 문자열 → { uri } 또는 { error }. throw 하지 않는다(호출부 분기 단순화).
 *
 * 스킴을 먼저 검사한 뒤에만 normalizeToOtpauthUri 를 호출하는 것이 이 함수의 안전 불변식이다.
 * 그 함수는 bare base32 secret 도 받아들이므로(services/totp/index.js), 순서를 뒤집으면
 * base32 문자만 담긴 임의 QR(명함·와이파이 등)이 조용히 유효 시드로 등록된다 — 'HELLO' 는
 * 실제로 3바이트 secret 생성에 성공한다(실측).
 *
 * 익스텐션이 후속 PR 에서 이 판정을 미러링하므로 순수 함수로 유지한다.
 */
export function classifyQrPayload(data) {
  const raw = String(data || '').trim()
  const lower = raw.toLowerCase()

  if (lower.startsWith(MIGRATION_SCHEME)) return { error: 'MIGRATION_QR' }
  if (!lower.startsWith(TOTP_SCHEME)) return { error: 'NOT_TOTP_QR' }

  try {
    return { uri: normalizeToOtpauthUri(raw) }
  } catch (e) {
    if (e?.code === 'UNSUPPORTED_TOTP') return { error: 'UNSUPPORTED' }
    // otpauth 스킴인데 파싱 실패 — 시드 부재와 그 밖의 파라미터 오류를 가른다.
    // OTPAuth.URI.parse 는 period/digits/algorithm 이 잘못돼도 throw 하므로(실측:
    // "Invalid 'period' parameter" 등), 전부 "인증 키가 없습니다" 로 안내하면 사용자가
    // 있지도 않은 원인을 찾는다 — D6(미지원 방식 문구 분리)와 같은 종류의 문제다.
    return { error: hasNonEmptySecretParam(raw) ? 'INVALID_QR' : 'EMPTY_SECRET' }
  }
}

/** 클립보드/파일 이미지 Blob → { uri } 또는 { error }. jsQR 은 쓰는 순간에만 로드한다. */
export async function decodeTotpQrImage(blob) {
  try {
    const imageData = await imageFileToImageData(blob)
    const { default: jsQR } = await import('jsqr')
    const result = jsQR(imageData.data, imageData.width, imageData.height)
    if (!result || !result.data) return { error: 'NO_QR' }
    return classifyQrPayload(result.data)
  } catch (e) {
    // 손상 이미지·canvas 미지원 등 — 사용자에겐 "QR 을 찾지 못했다" 로 동일 안내.
    // 시드가 섞일 수 있는 값은 로그에 남기지 않는다.
    return { error: 'NO_QR' }
  }
}
